import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { toast } from "react-toastify";
import SpeechToText from "src/components/SpeechToText";
import { X, Check } from "lucide-react";
import { requestMicPermission } from "src/utils/micPermission";
import { formatAIText } from "src/utils/formatAIText";
import {
    getAssistantImagesForDisplay,
    shouldHideAssistantSourceLine,
} from "src/utils/chatLogSource";
import { askQuestion, getChatSession, appendChatSessionMessages, createChatSession, renameChatSession, API_BASE_URL } from "../../services/api";
import type { ChatFeedbackReason, ChatFeedbackType, ChatSessionMessage } from "../../services/api";
import SopDialog from "../../components/SopDialog";
import ChatFeedback from "../../components/ChatFeedback";
import ChatDocumentIcon from "../../assets/icons/ChatDocumentIcon.svg";
import { useTimezoneFormatting } from "src/hooks/useTimezoneFormatting";
import { logger } from "src/utils/logger";
import {
    dedupeChatMessages,
    mapServerMessagesToChat,
    mergeChatMessages,
    type WorkerChatMessage,
} from "./chatMessageUtils";

type ChatMessage = WorkerChatMessage;

type MessageFeedbackEntry = {
    feedbackType: ChatFeedbackType;
    reason?: ChatFeedbackReason;
};

export type WorkerChatbotHandle = { startNewChat: () => void, hasMessages: () => boolean, getMessages: () => ChatSessionMessage[] };

type WorkerChatbotProps = { sessionId?: string | null, onMessagesChange?: (count: number) => void, onFirstTurnReady?: (messages: ChatSessionMessage[], newSessionId?: string) => void };

export const WorkerChatbot = forwardRef<WorkerChatbotHandle, WorkerChatbotProps>(({ sessionId, onMessagesChange, onFirstTurnReady }, ref) => {
    const { formatToLocal } = useTimezoneFormatting();
    const [message, setMessage] = useState("");
    const [isMicOpen, setIsMicOpen] = useState<boolean>(false);
    const [sttTranscript, setSttTranscript] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    const [pendingSessionIds, setPendingSessionIds] = useState<ReadonlySet<string>>(() => new Set());
    const [messageFeedbackByMessageId, setMessageFeedbackByMessageId] = useState<
        Record<string, MessageFeedbackEntry>
    >({});
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [sopDialog, setSopDialog] = useState<{
        isOpen: boolean;
        sopName: string;
        sectionTitle: string;
        documentContent?: string;
        pdfUrl?: string;
    }>({
        isOpen: false,
        sopName: '',
        sectionTitle: '',
        documentContent: undefined,
        pdfUrl: undefined
    });

    const messagesBySessionRef = useRef<Map<string, ChatMessage[]>>(new Map());
    const pendingSessionsRef = useRef<Set<string>>(new Set());
    const persistedMessageKeysRef = useRef<Set<string>>(new Set());
    const lastSessionIdRef = useRef<string | null>(null);
    const activeSessionIdRef = useRef<string | null>(sessionId ?? null);
    /** Session receiving a send before parent sets sessionId (new chat). */
    const localSendSessionIdRef = useRef<string | null>(null);

    useEffect(() => {
        activeSessionIdRef.current = sessionId ?? null;
        if (sessionId) {
            localSendSessionIdRef.current = null;
        }
    }, [sessionId]);

    const setSessionPending = useCallback((sid: string, pending: boolean) => {
        if (pending) {
            pendingSessionsRef.current.add(sid);
        } else {
            pendingSessionsRef.current.delete(sid);
        }
        setPendingSessionIds((prev) => {
            const next = new Set(prev);
            if (pending) {
                next.add(sid);
            } else {
                next.delete(sid);
            }
            return next;
        });
    }, []);

    const isDisplayedSession = useCallback((sid: string) => {
        const active = activeSessionIdRef.current;
        return active === sid || (active === null && localSendSessionIdRef.current === sid);
    }, []);

    const syncViewIfActive = useCallback((sid: string, messages: ChatMessage[]) => {
        if (isDisplayedSession(sid)) {
            setChatHistory(messages);
        }
    }, [isDisplayedSession]);

    const updateSessionMessages = useCallback((
        sid: string,
        updater: (prev: ChatMessage[]) => ChatMessage[],
    ) => {
        const prev = messagesBySessionRef.current.get(sid) ?? [];
        const next = dedupeChatMessages(updater(prev));
        messagesBySessionRef.current.set(sid, next);
        syncViewIfActive(sid, next);
        return next;
    }, [syncViewIfActive]);

    const isDailyLimitError = (err: unknown): boolean => {
        const msg = String((err as { message?: string })?.message || err || "");
        return /daily message limit/i.test(msg) && !/insufficient_quota/i.test(msg);
    };

    const getDailyResetTimeText = (): string => {
        const now = new Date();
        const reset = new Date(now);
        reset.setDate(now.getDate() + 1);
        reset.setHours(0, 0, 0, 0);
        const opts: Intl.DateTimeFormatOptions = {
            weekday: 'short', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        };
        return new Intl.DateTimeFormat(undefined, opts).format(reset);
    };

    const appendLimitMessage = useCallback((sid: string) => {
        const resetText = getDailyResetTimeText();
        const limitMsg: ChatMessage = {
            id: `${Date.now()}-limit`,
            role: 'assistant',
            answer: `Daily message limit reached. You can chat again on ${resetText}.`,
            timestamp: new Date().toISOString(),
        };
        updateSessionMessages(sid, (prev) => [...prev, limitMsg]);
        setError("");
    }, [updateSessionMessages]);

    useEffect(() => {
        const run = async () => {
            const normalizedSessionId = sessionId ?? null;
            const sessionChanged = lastSessionIdRef.current !== normalizedSessionId;

            if (!normalizedSessionId) {
                if (sessionChanged) {
                    lastSessionIdRef.current = null;
                    setChatHistory([]);
                }
                return;
            }

            if (sessionChanged) {
                lastSessionIdRef.current = normalizedSessionId;
                const cached = messagesBySessionRef.current.get(normalizedSessionId);
                setChatHistory(dedupeChatMessages(cached ?? []));
            }

            if (pendingSessionsRef.current.has(normalizedSessionId)) {
                return;
            }

            const cachedBeforeFetch = messagesBySessionRef.current.get(normalizedSessionId) ?? [];

            setIsLoadingHistory(true);
            try {
                const res = await getChatSession(normalizedSessionId);
                const msgs = mapServerMessagesToChat(res?.item?.messages || []);

                if (activeSessionIdRef.current !== normalizedSessionId) {
                    return;
                }

                const merged = mergeChatMessages(cachedBeforeFetch, msgs);
                messagesBySessionRef.current.set(normalizedSessionId, merged);
                setChatHistory(merged);
            } catch {
                // Keep cached messages on screen if fetch fails
            } finally {
                setIsLoadingHistory(false);
            }
        };
        run();
    }, [sessionId, pendingSessionIds]);

    const displaySessionId = sessionId ?? localSendSessionIdRef.current;
    const isActiveSessionPending =
        displaySessionId !== null && pendingSessionIds.has(displaySessionId);

    const showConversationLayout =
        chatHistory.length > 0 || isActiveSessionPending;

    const handleSendMessage = async () => {
        if (!message.trim()) return;

        let currentSessionId = sessionId ?? null;

        if (!currentSessionId) {
            try {
                const response = await createChatSession("New Worker Chat");
                currentSessionId = response.item.id;
                messagesBySessionRef.current.set(currentSessionId, []);
                localSendSessionIdRef.current = currentSessionId;

                if (typeof onFirstTurnReady === 'function') {
                    onFirstTurnReady([], currentSessionId);
                }
            } catch {
                return;
            }
        } else {
            localSendSessionIdRef.current = currentSessionId;
        }

        if (pendingSessionsRef.current.has(currentSessionId)) return;

        const question = message.trim();
        setMessage("");
        setError("");

        setSessionPending(currentSessionId, true);

        const userMsgId = `${Date.now()}-u`;
        const userMsg: ChatMessage = {
            id: userMsgId,
            messageId: userMsgId,
            role: "user",
            question,
            timestamp: new Date().toISOString(),
        };

        const priorLength = (messagesBySessionRef.current.get(currentSessionId) ?? []).length;
        updateSessionMessages(currentSessionId, (prev) => [...prev, userMsg]);

        try {
            try {
                if (priorLength === 0) {
                    const newTitle = question.length > 60 ? question.slice(0, 59) + '…' : question;
                    await renameChatSession(currentSessionId, newTitle);
                }
            } catch {
                // best-effort
            }

            let data;
            try {
                data = await askQuestion(question);

                if (data.answer && isDailyLimitError(data.answer)) {
                    appendLimitMessage(currentSessionId);
                    return;
                }
            } catch (e: unknown) {
                if (isDailyLimitError(e)) {
                    appendLimitMessage(currentSessionId);
                    return;
                }
                throw e;
            }

            const computedPdfUrl = data.pdfUrl
                ? `${API_BASE_URL}${data.pdfUrl}`
                : (data.sopSource && data.sopSource.fileId)
                    ? `${API_BASE_URL}/documents/${data.sopSource.fileId}/pdf`
                    : undefined;

            const aiMsgId = `${Date.now()}-a`;
            const aiMsg: ChatMessage = {
                id: aiMsgId,
                messageId: aiMsgId,
                role: "assistant",
                answer: data.answer,
                images: getAssistantImagesForDisplay(data.answer || '', data.images),
                timestamp: new Date().toISOString(),
                sopSource: data.sopSource || undefined,
                sourcedFrom: (data.sourcedFrom && (data.sourcedFrom.documentName || data.sourcedFrom.sectionName))
                    ? data.sourcedFrom
                    : (data.document_used ? { documentName: data.document_used } : undefined),
                pdfUrl: computedPdfUrl,
            };

            updateSessionMessages(currentSessionId, (prev) => [...prev, aiMsg]);

            const turnMessages: ChatSessionMessage[] = [
                { role: 'user', text: question, timestamp: userMsg.timestamp, messageId: userMsg.messageId },
                {
                    role: 'assistant',
                    text: aiMsg.answer || '',
                    images: aiMsg.images,
                    timestamp: aiMsg.timestamp,
                    messageId: aiMsg.messageId,
                    sopSource: aiMsg.sopSource,
                    sourcedFrom: aiMsg.sourcedFrom,
                    pdfUrl: aiMsg.pdfUrl,
                },
            ];

            const turnPersistKey = `${currentSessionId}:${userMsg.messageId}:${aiMsg.messageId}`;
            if (!persistedMessageKeysRef.current.has(turnPersistKey)) {
                persistedMessageKeysRef.current.add(turnPersistKey);
                appendChatSessionMessages(currentSessionId, turnMessages).catch((e: unknown) => {
                    persistedMessageKeysRef.current.delete(turnPersistKey);
                    if (isDailyLimitError(e)) {
                        appendLimitMessage(currentSessionId);
                    }
                });
            }
        } catch (e: unknown) {
            if (isDailyLimitError(e)) {
                appendLimitMessage(currentSessionId);
            } else {
                setError((e as { message?: string })?.message || "Failed to get answer. Please try again.");
            }
        } finally {
            setSessionPending(currentSessionId, false);
        }
    };

    const handleStartNewChat = useCallback(async () => {
        try {
            setChatHistory([]);
            if (typeof onFirstTurnReady === 'function') {
                onFirstTurnReady([], undefined);
            }
            setMessage("");
            setError("");
        } catch (err) {
            logger.error('Error starting new chat:', err);
        }
    }, [onFirstTurnReady]);

    const getMessages = useCallback((): ChatSessionMessage[] => {
        return chatHistory.map((m) => ({
            role: m.role,
            text: m.role === 'user' ? (m.question || '') : (m.answer || ''),
            images: m.images,
            timestamp: m.timestamp,
            messageId: m.messageId,
        }));
    }, [chatHistory]);

    useImperativeHandle(
        ref,
        () => ({
            startNewChat: handleStartNewChat,
            hasMessages: () => chatHistory.length > 0,
            getMessages,
        }),
        [handleStartNewChat, getMessages, chatHistory.length]
    );

    useEffect(() => {
        if (onMessagesChange) onMessagesChange(chatHistory.length);
    }, [chatHistory.length, onMessagesChange]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [chatHistory, isActiveSessionPending]);

    const formatTime = (dateString: string): string => {
        return formatToLocal(dateString, {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const handleViewSop = (sopSource: ChatMessage['sopSource'], pdfUrl?: string) => {
        if (sopSource) {
            const effectiveUrl = pdfUrl || (sopSource.fileId ? `${API_BASE_URL}/documents/${sopSource.fileId}/pdf` : undefined);

            setSopDialog({
                isOpen: true,
                sopName: sopSource.sopName,
                sectionTitle: sopSource.sectionTitle,
                documentContent: sopSource.documentContent,
                pdfUrl: effectiveUrl
            });
        }
    };

    const inputDisabled = isActiveSessionPending || isLoadingHistory;

    const renderChatInput = () => (
        <div className="ai-chat-input-container">
            {!isMicOpen ? (
                <textarea
                    rows={1}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Ask about procedures..."
                    className="ai-chat-input"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                    disabled={inputDisabled}
                    style={{
                        resize: 'none',
                        overflow: 'auto',
                        height: '45px',
                        minHeight: '45px',
                        maxHeight: '65px'
                    }}
                    onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = '45px';
                        target.style.height = Math.min(target.scrollHeight, 90) + 'px';
                    }}
                ></textarea>
            ) : (
                <div className="ai-chat-mic-pill">
                    <SpeechToText
                        className="min-w-0 flex-1 h-full flex items-center"
                        active={true}
                        height={29}
                        windowSec={2}
                        timesliceMs={40}
                        onTranscript={(text) => {
                            setSttTranscript(text);
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => {
                            setIsMicOpen(false);
                            setSttTranscript("");
                        }}
                        className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-gray-100 text-gray-700"
                        aria-label="Cancel voice input"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (sttTranscript.trim()) {
                                setMessage((prev) => (prev ? prev + ' ' : '') + sttTranscript.trim());
                            }
                            setSttTranscript("");
                            setIsMicOpen(false);
                        }}
                        className="flex-shrink-0 ml-1 p-1 rounded-full hover:bg-gray-100 text-gray-700"
                        aria-label="Accept voice input"
                    >
                        <Check className="w-5 h-5" />
                    </button>
                </div>
            )}
            {!isMicOpen && (
                <button
                    type="button"
                    onClick={async () => {
                        if (inputDisabled) return;
                        const result = await requestMicPermission();
                        if (!result.granted) {
                            toast.error(result.errorMessage);
                            return;
                        }
                        toast.success("Microphone is active. You can start speaking.");
                        setIsMicOpen(true);
                    }}
                    className="chat-mic-btn"
                    disabled={inputDisabled}
                    title="Click to use voice input"
                >
                    <i className="iconoir-microphone-solid"></i>
                </button>
            )}
            <button
                onClick={handleSendMessage}
                className="chat-send-btn"
                disabled={inputDisabled}
            >
                <i className="iconoir-send-solid"></i>
            </button>
        </div>
    );

    const renderMessageList = () => (
        <div ref={listRef} className="worker-chat-history">
            {chatHistory.map((m) => {
                const displayImages =
                    m.role === 'assistant'
                        ? getAssistantImagesForDisplay(m.answer || '', m.images)
                        : [];

                return (
                <div key={m.id} className="d-flex flex-column gap-2">
                    {m.role === "user" ? (
                        <div className="align-self-end" style={{ maxWidth: '80%' }}>
                            <div className="worker-sent-message">
                                {m.question}
                            </div>
                        </div>
                    ) : (
                        <div className="align-self-start">
                            <div className="worker-ai-message">
                                <div className="grey-700">
                                    {formatAIText(m.answer || "")}
                                </div>
                                {displayImages.length > 0 && (
                                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {displayImages.map((img, idx) => (
                                            <img key={idx} src={img} alt={`answer-${idx}`} className="rounded-lg border cursor-zoom-in" onClick={() => setSelectedImage(img)} />
                                        ))}
                                    </div>
                                )}

                                {((m.pdfUrl ||
                                    m.sopSource ||
                                    m.sourcedFrom?.documentName) &&
                                    !shouldHideAssistantSourceLine(m.answer || '') &&
                                    m.sourcedFrom?.documentName !== 'Unknown Document') && (
                                        <div className="mt-2">
                                            <p className="text-18reg grey-900 mb-2">
                                                📄&nbsp;
                                                {m.sopSource ? (
                                                    <span>
                                                        Sourced from SOP: {m.sopSource.sopName}, {m.sopSource.sectionTitle}
                                                    </span>
                                                ) : (
                                                    m.sourcedFrom?.documentName && (
                                                        <span>
                                                            Sourced from: {m.sourcedFrom.documentName}
                                                            {m.sourcedFrom.sectionName ? ` — ${m.sourcedFrom.sectionName}` : ''}
                                                        </span>
                                                    )
                                                )}
                                            </p>
                                            <div className="flex items-center gap-3">
                                                {(m.sopSource || m.pdfUrl) &&
                                                    m.sourcedFrom?.documentName !== 'Unknown Document' && (
                                                        <button
                                                            onClick={() => {
                                                                const sopSource =
                                                                    m.sopSource || {
                                                                        sopName:
                                                                            m.sourcedFrom?.documentName || 'Document',
                                                                        sectionTitle:
                                                                            m.sourcedFrom?.sectionName || '',
                                                                        fileId: 'unknown',
                                                                        documentContent: undefined,
                                                                    };

                                                                const url =
                                                                    m.pdfUrl ||
                                                                    (sopSource.fileId && sopSource.fileId !== 'unknown'
                                                                        ? `${API_BASE_URL}/documents/${sopSource.fileId}/pdf`
                                                                        : undefined);

                                                                if (!url) return;
                                                                handleViewSop(sopSource, url);
                                                            }}
                                                            className="sop-outline-btn"
                                                        >
                                                            → [Tap to View SOP]
                                                        </button>
                                                    )}
                                            </div>
                                        </div>
                                    )}

                            </div>
                            <div className="chat-message-footer">
                                <ChatFeedback
                                    messageId={m.messageId}
                                    sessionId={sessionId}
                                    persistedFeedback={
                                        m.messageId && messageFeedbackByMessageId[m.messageId]
                                            ? {
                                                feedbackType:
                                                    messageFeedbackByMessageId[m.messageId]
                                                        .feedbackType,
                                                reason:
                                                    messageFeedbackByMessageId[m.messageId]
                                                        .reason ?? null,
                                            }
                                            : undefined
                                    }
                                    onFeedbackCommitted={(payload) => {
                                        if (!m.messageId) return;
                                        setMessageFeedbackByMessageId((prev) => ({
                                            ...prev,
                                            [m.messageId!]:
                                                payload.feedbackType === "UP"
                                                    ? { feedbackType: "UP" }
                                                    : {
                                                        feedbackType: "DOWN",
                                                        reason: payload.reason,
                                                    },
                                        }));
                                    }}
                                />
                                <div className="text-[11px] text-gray-400">AI Assistant [{formatTime(m.timestamp)}]</div>
                            </div>
                        </div>
                    )}
                </div>
                );
            })}
            {isActiveSessionPending && (
                <div className="ai-thinking">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            )}
        </div>
    );

    return (
        <div className={`worker-chat-container ${showConversationLayout ? 'has-chat-history' : 'no-chat-history'}`}>
            <div className="flex-1 d-flex flex-column p-6 overflow-hidden min-h-0">
                {error && (
                    <div className="mb-3 text-sm text-red-600 flex-shrink-0">{error}</div>
                )}

                {!showConversationLayout && !isLoadingHistory ? (
                    <div className="chat-empty-layout">
                        <div className="chat-empty-hero text-center text-gray-600">
                            <div className="text-center">
                                <img className="mb-3 d-md-none mx-auto" src={ChatDocumentIcon} alt="Chat document icon" />
                                <h1 className="grey-800">Ask about tools, safety <br />steps, or checklists.</h1>
                            </div>
                        </div>
                        {renderChatInput()}
                    </div>
                ) : isLoadingHistory && chatHistory.length === 0 && !isActiveSessionPending ? (
                    <div className="flex-1 d-flex flex-column align-items-center justify-content-center text-center grey-600">
                        <div className="w-14 h-14 rounded-circle bg-light d-flex align-items-center justify-content-center mb-3">
                            <div className="animate-spin rounded-circle h-6 w-6 border-b-2 grey-500"></div>
                        </div>
                        <h3 className="text-20-bold mb-1">Loading chat history...</h3>
                        <p className="text-14-reg">Please wait while we fetch your previous conversations.</p>
                    </div>
                ) : (
                    <>
                        {renderMessageList()}
                        {renderChatInput()}
                    </>
                )}
            </div>

            {selectedImage && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1060]" onClick={() => setSelectedImage(null)}>
                    <div className="max-w-3xl w-full px-4" onClick={(e) => e.stopPropagation()}>
                        <img src={selectedImage} alt="Selected" className="w-full rounded-lg shadow-lg" />
                    </div>
                </div>
            )}

            <SopDialog
                isOpen={sopDialog.isOpen}
                onClose={() => setSopDialog(prev => ({ ...prev, isOpen: false }))}
                sopName={sopDialog.sopName}
                sectionTitle={sopDialog.sectionTitle}
                documentContent={sopDialog.documentContent}
                // @ts-ignore
                pdfUrl={sopDialog.pdfUrl}
            />
        </div>
    );
});
